// Impacts: crater carving into building canvases, plus the particle burst.

import { state } from './state.js';
import { clamp, rand } from './utils.js';
import { playBoom } from './audio.js';
import { mods } from './modes.js';
import { localStream } from './rng.js';

const CRATER_CHIPS = 5;
const CRATER_CHIP_RATIO = 0.45;
const SCORCH_RIM = 5;

const SMOKE_PUFFS = 5;
const DEBRIS_COUNT = 14;

// Per-frame growth and fade for the particles a blast spawns. These live here
// rather than in render/fx.js because the spawner owns the lifetime: keeping
// the numbers beside the burst they belong to is what makes them tunable.
//
// A particle lives for alpha / fade frames, so lowering alpha shortens its life
// unless the fade is lowered too — worth remembering when retuning the cloud.
const SMOKE_DRIFT = 0.4;
const SMOKE_FADE = 0.02;
const FIREBALL_FADE = 0.045;

const SMALL_BLAST = { shake: 9, flash: 0.3, radius: 6, growth: 2 };

// A direct hit is not an explosion, it is a small nuclear event — and it goes
// off on the gorilla itself, not wherever the banana happened to be when the
// hit was detected.
const NUKE = {
  shake: 26,
  flash: 0.85,
  core: { radius: 14, growth: 3.2, fade: 0.013 },
  wave: { radius: 8, growth: 9, fade: 0.022 },
  stemPuffs: 26,
  capPuffs: 34,
  debris: 60,
};

/** Height of a full-size mushroom cloud. */
const CLOUD_HEIGHT = 80;
/** Headroom above the blast that earns the cloud its full size. */
const CLOUD_HEADROOM = 210;
/** Y the top of the cloud has to stay below, to clear the score panels. */
const CLOUD_CLEARANCE = 78;

// Carve a real crater (transparent hole) into a building's own canvas, so the
// damage is persistent and bananas can fly through windows.
export function crater(b, wx, wy, r) {
  // The geometry is quantised to exactly what a shared code carries — whole
  // pixels for the centre, one decimal for the radius. Cutting the hole at full
  // precision instead would make a rebuilt crater a hair wider than the original,
  // and a banana would sail through a gap that is not quite there.
  const cx = Math.round(wx);
  const cy = Math.round(wy);
  const radius = Math.round(r * 10) / 10;

  // Remembered so a match can be sent to someone else and rebuilt exactly.
  state.craters.push({ x: cx, y: cy, r: radius });

  const lx = cx - b.x;
  const ly = cy - b.y;
  const bc = b.bctx;

  // The chips are rolled from the crater's own numbers, never from the global
  // stream, so this hole has the same ragged edge wherever it is cut.
  const roll = localStream(cx, cy, Math.round(radius * 10));
  const chip = () => (roll() * 2 - 1) * radius * CRATER_CHIP_RATIO;

  bc.save();
  bc.globalCompositeOperation = 'destination-out';
  bc.beginPath();
  bc.arc(lx, ly, radius, 0, Math.PI * 2);
  bc.fill();
  for (let i = 0; i < CRATER_CHIPS; i++) {
    bc.beginPath();
    bc.arc(lx + chip(), ly + chip(), radius * CRATER_CHIP_RATIO, 0, Math.PI * 2);
    bc.fill();
  }
  bc.restore();

  // scorched rim
  bc.save();
  bc.globalCompositeOperation = 'source-atop';
  bc.fillStyle = 'rgba(20,10,6,.5)';
  bc.beginPath();
  bc.arc(lx, ly, r + SCORCH_RIM, 0, Math.PI * 2);
  bc.fill();
  bc.restore();
}

/** Blast where a banana meets a building: small, grey and short-lived. */
export function explode(px, py) {
  // Demolition mode throws a lot more of the building around, which is most of
  // what makes it feel different from the usual miss.
  const heavy = mods().debrisScale;

  state.shake = SMALL_BLAST.shake * (1 + (heavy - 1) * 0.35);
  state.flash = SMALL_BLAST.flash;

  state.fireballs.push({
    x: px,
    y: py,
    r: SMALL_BLAST.radius,
    gr: SMALL_BLAST.growth,
    fade: FIREBALL_FADE,
    a: 1,
  });

  for (let i = 0; i < Math.round(SMOKE_PUFFS * heavy); i++) {
    state.smoke.push({
      x: px + rand(-6, 6),
      y: py,
      r: rand(4, 8),
      vy: rand(1, 2) * 0.6,
      vx: 0,
      dr: SMOKE_DRIFT,
      fa: SMOKE_FADE,
      c: '#555c73',
      a: 0.5,
    });
  }

  for (let i = 0; i < Math.round(DEBRIS_COUNT * heavy); i++) {
    state.debris.push({
      x: px,
      y: py,
      vx: rand(-4, 4),
      vy: rand(-5, -1),
      s: rand(2, 4),
      c: i % 2 ? '#ff9d55' : '#9aa2bc',
    });
  }

  if (state.audioCtx) playBoom(false);
}

/**
 * A direct hit on a gorilla: a fireball, an expanding shockwave and a proper
 * mushroom cloud, all centred on the gorilla so the blast reads as the gorilla
 * going up rather than the banana popping next to it.
 */
export function nuke(x, y) {
  state.shake = NUKE.shake;
  state.flash = NUKE.flash;

  state.fireballs.push({ x, y, r: NUKE.core.radius, gr: NUKE.core.growth, fade: NUKE.core.fade, a: 1 });
  state.shockwaves.push({ x, y, r: NUKE.wave.radius, dr: NUKE.wave.growth, fa: NUKE.wave.fade, a: 1 });

  // A gorilla can be perched as high as y≈185, so the mushroom is scaled to the
  // headroom above the blast instead of assuming a fixed height — a fixed one
  // throws the cloud straight off the top of the canvas on a high perch.
  const scale = clamp((y - CLOUD_CLEARANCE) / CLOUD_HEADROOM, 0.4, 1);

  // Stem: a column of smoke climbing out of the fireball. It is left fairly
  // translucent so the fireball glows through it from underneath.
  for (let i = 0; i < NUKE.stemPuffs; i++) {
    const climb = i / NUKE.stemPuffs;
    state.smoke.push({
      x: x + rand(-16, 16) * scale,
      y: y - climb * CLOUD_HEIGHT * scale + rand(-6, 6) * scale,
      r: rand(10, 20) * scale,
      vy: rand(4, 9) * 0.1 * scale,
      vx: rand(-3, 3) * 0.1,
      dr: 0.55 * scale,
      fa: 0.008,
      c: '#6f6a78',
      a: 0.6,
    });
  }

  // Cap: wider, slower and longer-lived, so it keeps spreading sideways while
  // the stem catches up with it.
  for (let i = 0; i < NUKE.capPuffs; i++) {
    const side = i % 2 ? 1 : -1;
    state.smoke.push({
      x: x + side * rand(0, 52) * scale,
      y: y - CLOUD_HEIGHT * scale + rand(-22, 22) * scale,
      r: rand(14, 24) * scale,
      vy: rand(1, 3) * 0.1 * scale,
      vx: side * rand(2, 8) * 0.1,
      dr: 0.7 * scale,
      fa: 0.009,
      c: '#7b7f96',
      a: 0.7,
    });
  }

  for (let i = 0; i < NUKE.debris; i++) {
    state.debris.push({
      x,
      y,
      vx: rand(-11, 11),
      vy: rand(-13, -2),
      s: rand(2, 5),
      c: i % 3 === 0 ? '#ffd9a0' : i % 3 === 1 ? '#ff9d55' : '#9aa2bc',
    });
  }

  if (state.audioCtx) playBoom(true);
}
